/** Frontend API wrapper for the insurance policies held by a subcontractor.
 *
 * The routes are workspace-admin only; a refused caller gets a
 * SubcontractorCoverError with status 403 so the screen can say so plainly.
 */

import { API_BASE, getAuthHeaders } from '../utils/supabase/client';
import type { CoverPayload, ExpiringSubcontractorCover, SubcontractorCover } from '../features/subcontractors/subcontractorCover';
import { readError } from './_http';

export class SubcontractorCoverError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function fail(res: Response): Promise<never> {
  throw new SubcontractorCoverError(await readError(res), res.status);
}

export class SubcontractorCoverService {
  static async list(subcontractorId: string): Promise<SubcontractorCover[]> {
    const res = await fetch(
      `${API_BASE}/subcontractor-cover?subcontractorId=${encodeURIComponent(subcontractorId)}`,
      { headers: await getAuthHeaders() },
    );
    if (!res.ok) return fail(res);
    return res.json();
  }

  /** Active policies already expired or ending within `days`, every page followed. */
  static async listExpiring(workspaceId: string, days = 60, signal?: AbortSignal): Promise<ExpiringSubcontractorCover[]> {
    const cover: ExpiringSubcontractorCover[] = [];
    let offset = 0;
    do {
      const res = await fetch(
        `${API_BASE}/subcontractor-cover/expiring?workspaceId=${encodeURIComponent(workspaceId)}&days=${days}&offset=${offset}`,
        { headers: await getAuthHeaders(), signal },
      );
      if (!res.ok) return fail(res);
      const page: { cover: ExpiringSubcontractorCover[]; nextOffset: number | null } = await res.json();
      if (!Array.isArray(page.cover) || (page.nextOffset !== null && (!Number.isSafeInteger(page.nextOffset) || page.nextOffset <= offset))) {
        throw new Error('Invalid insurance cover expiry page');
      }
      cover.push(...page.cover);
      if (page.nextOffset === null) return cover;
      offset = page.nextOffset;
    } while (true);
  }

  static async add(subcontractorId: string, data: CoverPayload): Promise<SubcontractorCover> {
    const res = await fetch(`${API_BASE}/subcontractor-cover`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, subcontractorId }),
    });
    if (!res.ok) return fail(res);
    return res.json();
  }

  static async update(
    id: string,
    data: Partial<CoverPayload> & { lifecycleStatus?: 'active' | 'superseded' },
  ): Promise<SubcontractorCover> {
    const res = await fetch(`${API_BASE}/subcontractor-cover/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) return fail(res);
    return res.json();
  }
}
